import { useEffect, useMemo, useRef, useState } from 'react'
import {
  downloadPlatformTenantStoreBuild,
  getPlatformTenant,
  patchPlatformTenantStoreApp,
  resetPlatformTenantStoreBuild,
  triggerPlatformTenantStoreBuild,
  type PlatformTenantStoreApp,
  type PlatformTenantStoreAppBuild,
  type PlatformTenantStoreAppStatus,
  type PlatformTenantStoreBuildStatus,
} from '@/api/platformApi'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Platform.module.css'

const STATUS_OPTIONS: { value: PlatformTenantStoreAppStatus; label: string }[] = [
  { value: 'rascunho', label: 'Rascunho' },
  { value: 'pronto', label: 'Pronto para build' },
  { value: 'enviado', label: 'Enviado às lojas' },
  { value: 'live', label: 'Publicado (live)' },
]

type StoreAppForm = {
  app_name: string
  app_short_name: string
  android_app_id: string
  ios_bundle_id: string
  icon_url: string
  listing_description: string
  status: PlatformTenantStoreAppStatus
  play_url: string
  appstore_url: string
}

function formFromStoreApp(store: PlatformTenantStoreApp | undefined, tenantNome: string): StoreAppForm {
  return {
    app_name: store?.app_name ?? (tenantNome ? `${tenantNome} – GoBooking` : ''),
    app_short_name: store?.app_short_name ?? tenantNome ?? '',
    android_app_id: store?.android_app_id ?? '',
    ios_bundle_id: store?.ios_bundle_id ?? '',
    icon_url: store?.icon_url ?? '',
    listing_description: store?.listing_description ?? '',
    status: store?.status ?? 'rascunho',
    play_url: store?.play_url ?? '',
    appstore_url: store?.appstore_url ?? '',
  }
}

const BUILD_STATUS_LABELS: Record<PlatformTenantStoreBuildStatus, string> = {
  idle: 'Nunca gerado',
  queued: 'Na fila (GitHub Actions)',
  building: 'A compilar…',
  ready: 'Pacote pronto',
  failed: 'Falhou',
}

type Props = {
  tenantId: number
  tenantSlug: string
  tenantNome: string
  storeApp: PlatformTenantStoreApp | undefined
  onSaved: (storeApp: PlatformTenantStoreApp) => void
}

function defaultBuild(): PlatformTenantStoreAppBuild {
  return {
    status: 'idle',
    run_id: null,
    run_url: null,
    aab_ready: false,
    ipa_ready: false,
    error: null,
    started_at: null,
    finished_at: null,
  }
}

export function PlatformTenantStoreAppSection({
  tenantId,
  tenantSlug,
  tenantNome,
  storeApp,
  onSaved,
}: Props) {
  const [form, setForm] = useState<StoreAppForm>(() => formFromStoreApp(storeApp, tenantNome))
  const [saving, setSaving] = useState(false)
  const [building, setBuilding] = useState(false)
  const [downloading, setDownloading] = useState<'aab' | 'ipa' | null>(null)
  const [build, setBuild] = useState<PlatformTenantStoreAppBuild>(
    () => storeApp?.build ?? defaultBuild()
  )
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const { notifySaved, notifyError } = useSaveFeedback()

  useEffect(() => {
    setForm(formFromStoreApp(storeApp, tenantNome))
    setBuild(storeApp?.build ?? defaultBuild())
  }, [storeApp, tenantNome])

  useEffect(() => {
    const active = build.status === 'queued' || build.status === 'building'
    if (!active) {
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = null
      return
    }
    pollRef.current = setInterval(async () => {
      try {
        const detail = await getPlatformTenant(tenantId)
        const next = detail.store_app?.build ?? defaultBuild()
        setBuild(next)
        onSaved(detail.store_app)
      } catch {
        /* ignore poll errors */
      }
    }, 8000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [build.status, tenantId, onSaved])

  const buildSnippet = useMemo(() => {
    const lines = [
      `# Build web (slug fixo desta conta)`,
      `VITE_TENANT_SLUG=${tenantSlug} npm run build`,
      '',
      `# Capacitor (fase seguinte — usar os IDs abaixo)`,
      form.android_app_id ? `# Android: ${form.android_app_id}` : '# Android applicationId: (preencher)',
      form.ios_bundle_id ? `# iOS: ${form.ios_bundle_id}` : '# iOS bundleId: (preencher)',
      form.app_name ? `# Nome na loja: ${form.app_name}` : '',
    ].filter(Boolean)
    return lines.join('\n')
  }, [tenantSlug, form.android_app_id, form.ios_bundle_id, form.app_name])

  const iconPreview = form.icon_url.trim() ? resolveMediaUrl(form.icon_url.trim()) : ''

  const handleCopyBuild = async () => {
    try {
      await navigator.clipboard.writeText(buildSnippet)
      notifySaved('Comandos copiados.')
    } catch {
      notifyError(new Error('copy'), 'Não foi possível copiar.')
    }
  }

  const hasIosBundle = Boolean(form.ios_bundle_id.trim())
  const canGenerate =
    Boolean(form.app_name.trim() && form.android_app_id.trim()) &&
    !['queued', 'building'].includes(build.status)

  const saveForm = async () => {
    const updated = await patchPlatformTenantStoreApp(tenantId, {
      app_name: form.app_name.trim() || null,
      app_short_name: form.app_short_name.trim() || null,
      android_app_id: form.android_app_id.trim() || null,
      ios_bundle_id: form.ios_bundle_id.trim() || null,
      icon_url: form.icon_url.trim() || null,
      listing_description: form.listing_description.trim() || null,
      status: form.status,
      play_url: form.play_url.trim() || null,
      appstore_url: form.appstore_url.trim() || null,
    })
    onSaved(updated.store_app)
    return updated
  }

  const handleGenerate = async () => {
    setBuilding(true)
    try {
      await saveForm()
      const result = await triggerPlatformTenantStoreBuild(tenantId)
      const next = result.tenant.store_app
      setBuild(next.build ?? defaultBuild())
      onSaved(next)
      notifySaved('Build iniciado — pode demorar 10–20 minutos.')
    } catch (err) {
      notifyError(err, 'Erro ao iniciar geração do pacote.')
    } finally {
      setBuilding(false)
    }
  }

  const handleResetBuild = async () => {
    setBuilding(true)
    try {
      const result = await resetPlatformTenantStoreBuild(tenantId)
      const next = result.tenant.store_app
      setBuild(next?.build ?? defaultBuild())
      if (next) onSaved(next)
      notifySaved('Estado do build reposto. Pode gerar novamente.')
    } catch (err) {
      notifyError(err, 'Erro ao repor estado do build.')
    } finally {
      setBuilding(false)
    }
  }

  const handleDownload = async (kind: 'aab' | 'ipa') => {
    setDownloading(kind)
    try {
      await downloadPlatformTenantStoreBuild(tenantId, kind)
    } catch (err) {
      notifyError(err, 'Erro ao transferir pacote.')
    } finally {
      setDownloading(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await saveForm()
      notifySaved('Configuração da app guardada.')
    } catch (err) {
      notifyError(err, 'Erro ao guardar configuração da app.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className={styles.detailCard} aria-labelledby="det-store-app">
      <h2 id="det-store-app" className={styles.detailCardTitle}>
        App nas lojas (white-label)
      </h2>
      <p className={styles.detailLead}>
        Configuração para publicar uma app com a marca deste cliente na Google Play e App Store. O{' '}
        <strong>tema visual</strong> continua a ser definido pelo admin da conta no painel deles. Slug
        técnico fixo no build: <code>{tenantSlug}</code>.
      </p>

      <form className={styles.detailEditForm} onSubmit={handleSubmit}>
        <div className={styles.detailEditGrid}>
          <div className={styles.platField}>
            <label className={styles.platLabel} htmlFor="store-app-name">
              Nome na loja
            </label>
            <input
              id="store-app-name"
              className={styles.platInput}
              value={form.app_name}
              onChange={(e) => setForm((f) => ({ ...f, app_name: e.target.value }))}
              placeholder="Ex.: Tami Grupo – GoBooking"
            />
          </div>
          <div className={styles.platField}>
            <label className={styles.platLabel} htmlFor="store-app-short">
              Nome curto (ícone no telemóvel)
            </label>
            <input
              id="store-app-short"
              className={styles.platInput}
              value={form.app_short_name}
              onChange={(e) => setForm((f) => ({ ...f, app_short_name: e.target.value }))}
              placeholder="Ex.: Tami Grupo"
              maxLength={40}
            />
          </div>
        </div>

        <div className={styles.detailEditGrid}>
          <div className={styles.platField}>
            <label className={styles.platLabel} htmlFor="store-android-id">
              Android applicationId
            </label>
            <input
              id="store-android-id"
              className={styles.platInput}
              value={form.android_app_id}
              onChange={(e) => setForm((f) => ({ ...f, android_app_id: e.target.value }))}
              placeholder="pt.gobooking.tamigrupo"
              spellCheck={false}
            />
            <p className={styles.storeAppHint}>
              Não use palavras reservadas Java no ID (ex.: <code>default</code>,{' '}
              <code>class</code>). Prefira <code>pt.gobooking.cliente</code> em vez de{' '}
              <code>pt.gobooking.default</code>.
            </p>
          </div>
          <div className={styles.platField}>
            <label className={styles.platLabel} htmlFor="store-ios-id">
              iOS bundle ID (opcional)
            </label>
            <input
              id="store-ios-id"
              className={styles.platInput}
              value={form.ios_bundle_id}
              onChange={(e) => setForm((f) => ({ ...f, ios_bundle_id: e.target.value }))}
              placeholder="pt.gobooking.tamigrupo"
              spellCheck={false}
            />
          </div>
        </div>

        <div className={styles.platField}>
          <label className={styles.platLabel} htmlFor="store-icon-url">
            URL do ícone (1024×1024)
          </label>
          <input
            id="store-icon-url"
            className={styles.platInput}
            value={form.icon_url}
            onChange={(e) => setForm((f) => ({ ...f, icon_url: e.target.value }))}
            placeholder="/uploads/tenant-1/icon.png ou URL completa"
            spellCheck={false}
          />
          <p className={styles.storeAppHint}>
            Pode usar o logo que o admin carregou em Identidade — cole aqui o caminho da imagem.
          </p>
          {iconPreview ? (
            <img src={iconPreview} alt="" className={styles.storeAppIconPreview} />
          ) : null}
        </div>

        <div className={styles.platField}>
          <label className={styles.platLabel} htmlFor="store-desc">
            Descrição para a loja
          </label>
          <textarea
            id="store-desc"
            className={styles.platTextarea}
            rows={4}
            value={form.listing_description}
            onChange={(e) => setForm((f) => ({ ...f, listing_description: e.target.value }))}
            placeholder="Texto para Google Play / App Store…"
          />
        </div>

        <div className={styles.platField}>
          <label className={styles.platLabel} htmlFor="store-status">
            Estado da publicação
          </label>
          <select
            id="store-status"
            className={styles.platInput}
            value={form.status}
            onChange={(e) =>
              setForm((f) => ({ ...f, status: e.target.value as PlatformTenantStoreAppStatus }))
            }
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.detailEditGrid}>
          <div className={styles.platField}>
            <label className={styles.platLabel} htmlFor="store-play-url">
              Link Google Play
            </label>
            <input
              id="store-play-url"
              className={styles.platInput}
              value={form.play_url}
              onChange={(e) => setForm((f) => ({ ...f, play_url: e.target.value }))}
              placeholder="https://play.google.com/store/apps/details?id=…"
            />
          </div>
          <div className={styles.platField}>
            <label className={styles.platLabel} htmlFor="store-ios-url">
              Link App Store
            </label>
            <input
              id="store-ios-url"
              className={styles.platInput}
              value={form.appstore_url}
              onChange={(e) => setForm((f) => ({ ...f, appstore_url: e.target.value }))}
              placeholder="https://apps.apple.com/app/id…"
            />
          </div>
        </div>

        <div className={styles.storeBuildBox}>
          <div className={styles.storeBuildHeader}>
            <span className={styles.storeBuildTitle}>Gerar pacote nativo</span>
            <span className={`${styles.storeBuildBadge} ${styles[`storeBuildBadge_${build.status}`]}`}>
              {BUILD_STATUS_LABELS[build.status]}
            </span>
          </div>
          <p className={styles.storeAppHint}>
            Gera AAB (Android) e, se o bundle iOS estiver preenchido, IPA via GitHub Actions + Fastlane
            (10–20 min). O IPA é enviado automaticamente para o TestFlight e fica também disponível em
            «Transferir IPA». Requer <code>GITHUB_BUILD_TOKEN</code>, secrets{' '}
            <code>STORE_BUILD_UPLOAD_*</code>, <code>APPLE_TEAM_ID</code> e{' '}
            <code>APP_STORE_CONNECT_*</code> (ver <code>docs/native-store-build.md</code>).
          </p>
          {build.error ? (
            <p className={styles.erro} role="alert">
              {build.error}
            </p>
          ) : null}
          <div className={styles.storeBuildActions}>
            <button
              type="button"
              className={styles.btnGradient}
              disabled={!canGenerate || building || saving}
              onClick={handleGenerate}
            >
              {building
                ? 'A iniciar…'
                : hasIosBundle
                  ? 'Gerar pacotes (Android + iOS)'
                  : 'Gerar pacote Android (AAB)'}
            </button>
            {['queued', 'building', 'failed'].includes(build.status) ? (
              <button
                type="button"
                className={styles.btnSm}
                disabled={building || saving}
                onClick={handleResetBuild}
              >
                Repor estado
              </button>
            ) : null}
            {build.run_url && (build.status === 'queued' || build.status === 'building') ? (
              <a
                href={build.run_url}
                target="_blank"
                rel="noreferrer"
                className={styles.btnSm}
              >
                Ver no GitHub
              </a>
            ) : null}
            {build.aab_ready ? (
              <button
                type="button"
                className={styles.btnSm}
                disabled={downloading === 'aab'}
                onClick={() => handleDownload('aab')}
              >
                {downloading === 'aab' ? 'A transferir…' : 'Transferir AAB'}
              </button>
            ) : null}
            {build.ipa_ready ? (
              <button
                type="button"
                className={styles.btnSm}
                disabled={downloading === 'ipa'}
                onClick={() => handleDownload('ipa')}
              >
                {downloading === 'ipa' ? 'A transferir…' : 'Transferir IPA'}
              </button>
            ) : null}
          </div>
        </div>

        <div className={styles.storeBuildBox}>
          <div className={styles.storeBuildHeader}>
            <span className={styles.storeBuildTitle}>Referência de build manual</span>
            <button type="button" className={styles.btnSm} onClick={handleCopyBuild}>
              Copiar
            </button>
          </div>
          <pre className={styles.storeBuildPre}>{buildSnippet}</pre>
        </div>

        <div className={styles.detailEditActions}>
          <button type="submit" className={styles.btnGradient} disabled={saving}>
            {saving ? 'A guardar…' : 'Guardar configuração da app'}
          </button>
        </div>
      </form>
    </section>
  )
}
